// src/reports/BinLocationsReport.jsx — Reporting → Location Reports → Bin Locations Report
// Spec: docs/superpowers/specs/2026-09-13-bin-locations-report-design.md
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { Card, BlockStack, InlineStack, Text, Button, TextField, Banner, Modal, Badge, ButtonGroup, Checkbox } from '@shopify/polaris';
import ReactECharts from 'echarts-for-react';
import { ReportPicker } from '../Reporting';
import { useAuthFetch, useAuth } from '../AuthComponent';
import BinDefinitionModal from './BinDefinitionModal';

import { hasRole } from '../constants/roles';
const fmtN = n => (n === null || n === undefined) ? '—' : Number(n).toLocaleString();
const fmtPct = n => (n === null || n === undefined) ? '—' : `${Number(n).toFixed(1)}%`;
function fmtRun(ranAt) {
    if (!ranAt) return 'never';
    const d = new Date(ranAt);
    const t = d.toLocaleTimeString('en-US', { timeZone: 'America/New_York', hour: 'numeric', minute: '2-digit' });
    const day = d.toLocaleDateString('en-US', { timeZone: 'America/New_York', month: 'numeric', day: 'numeric', year: 'numeric' });
    return `${t} ${day}`;
}

export default function BinLocationsReport() {
    const fetchAuth = useAuthFetch();
    const { user } = useAuth();
    const { storeSlug } = useParams();
    const storeKey = storeSlug || 'soccerpost';
    const [searchParams, setSearchParams] = useSearchParams();
    const [locations, setLocations] = useState([]);
    const [locId, setLocId] = useState(searchParams.get('location') || '');
    const [summary, setSummary] = useState(null);
    const [history, setHistory] = useState([]);
    const [bins, setBins] = useState({ rows: [], total: 0 });
    const [unrec, setUnrec] = useState([]);
    const [filters, setFilters] = useState({ aisle: searchParams.get('aisle') || '', status: searchParams.get('status') || '', q: searchParams.get('q') || '', multi: searchParams.get('multi') === '1', page: 1 });
    // 2026-09-13 (Eric): every column sortable — bins table sorts server-side
    // (the page is a slice of the whole sorted set), aisle table client-side.
    const [binSort, setBinSort] = useState({ key: searchParams.get('sort') || 'bin', dir: searchParams.get('dir') === 'desc' ? 'desc' : 'asc' });
    const [aisleSort, setAisleSort] = useState({ key: 'aisle', dir: 'asc' });
    const [drill, setDrill] = useState(null);       // { bin, display, labels, segments, items }
    const [defineOpen, setDefineOpen] = useState(false);
    const [err, setErr] = useState(null);
    const [busy, setBusy] = useState(false);
    const canDefine = hasRole(user, ['admin', 'site_manager']);
    const isAdmin = hasRole(user, ['admin']);

    const loadLocations = useCallback(async () => {
        const r = await fetchAuth(`/api/reports/bin-locations/locations?store=${storeKey}`);
        const j = await r.json();
        if (!r.ok) throw new Error(j.detail || `HTTP ${r.status}`);
        const list = j.locations || [];
        setLocations(list);
        // Switching store (Soccer Post ↔ Surf) keeps the old location id in the
        // URL; if it is not one of this store's locations, fall back to the first.
        if (!list.some(l => String(l.location_id) === String(locId))) {
            setLocId(list.length ? String(list[0].location_id) : '');
            setFilters(f => ({ ...f, aisle: '', page: 1 }));
            setSummary(null); setBins({ rows: [], total: 0 }); setUnrec([]); setHistory([]);
        }
    }, [fetchAuth, storeKey, locId]);

    const loadAll = useCallback(async () => {
        if (!locId || !locations.some(l => String(l.location_id) === String(locId))) return;
        setBusy(true); setErr(null);
        try {
            const q = new URLSearchParams({ store: storeKey, location_id: locId });
            const [s, h, u] = await Promise.all([
                fetchAuth(`/api/reports/bin-locations/summary?${q}`).then(r => r.json()),
                fetchAuth(`/api/reports/bin-locations/history?${q}`).then(r => r.json()),
                fetchAuth(`/api/reports/bin-locations/unrecognized?${q}`).then(r => r.json()),
            ]);
            if (s.detail) throw new Error(s.detail);
            setSummary(s); setHistory(h.points || []); setUnrec(u.rows || []);
        } catch (e) { setErr(e.message); }
        finally { setBusy(false); }
    }, [fetchAuth, storeKey, locId, locations]);

    const loadBins = useCallback(async () => {
        if (!locId || !locations.some(l => String(l.location_id) === String(locId))) return;
        const q = new URLSearchParams({ store: storeKey, location_id: locId, page: String(filters.page), page_size: '200', sort: binSort.key, dir: binSort.dir });
        if (filters.aisle) q.set('aisle', filters.aisle);
        if (filters.status) q.set('status', filters.status);
        if (filters.q) q.set('q', filters.q);
        if (filters.multi) q.set('multi', '1');
        const r = await fetchAuth(`/api/reports/bin-locations/bins?${q}`);
        const j = await r.json();
        if (r.ok) setBins(j);
    }, [fetchAuth, storeKey, locId, filters, binSort, locations]);

    useEffect(() => { loadLocations().catch(e => setErr(e.message)); }, [loadLocations]);
    useEffect(() => { loadAll(); }, [loadAll]);
    useEffect(() => { loadBins(); }, [loadBins]);
    useEffect(() => {
        const p = new URLSearchParams();
        if (locId) p.set('location', locId);
        if (filters.aisle) p.set('aisle', filters.aisle);
        if (filters.status) p.set('status', filters.status);
        if (filters.q) p.set('q', filters.q);
        if (filters.multi) p.set('multi', '1');
        if (binSort.key !== 'bin' || binSort.dir !== 'asc') { p.set('sort', binSort.key); p.set('dir', binSort.dir); }
        setSearchParams(p, { replace: true });
    }, [locId, filters, binSort, setSearchParams]);

    // poll while a refresh is running
    useEffect(() => {
        if (!summary?.refreshing) return undefined;
        const t = setInterval(() => { loadAll(); loadBins(); }, 15000);
        return () => clearInterval(t);
    }, [summary?.refreshing, loadAll, loadBins]);

    const refreshNow = async () => {
        const r = await fetchAuth('/api/reports/bin-locations/refresh', { method: 'POST', headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ store: storeKey, location_id: parseInt(locId, 10) }) });
        const j = await r.json();
        if (!r.ok) return setErr(j.detail || `HTTP ${r.status}`);
        setSummary(s => ({ ...(s || {}), refreshing: true }));
    };

    const openBin = async (bin) => {
        const q = new URLSearchParams({ store: storeKey, location_id: locId, bin });
        const r = await fetchAuth(`/api/reports/bin-locations/bin?${q}`);
        const j = await r.json();
        if (r.ok) setDrill(j);
    };

    const exportCsv = (filtered) => {
        const q = new URLSearchParams({ store: storeKey, location_id: locId });
        if (filtered) {
            if (filters.aisle) q.set('aisle', filters.aisle);
            if (filters.status) q.set('status', filters.status);
            if (filters.q) q.set('q', filters.q);
            if (filters.multi) q.set('multi', '1');
        }
        fetchAuth(`/api/reports/bin-locations/export.csv?${q}`).then(r => r.blob()).then(b => {
            const a = document.createElement('a'); a.href = URL.createObjectURL(b);
            a.download = `bin_locations_${filtered ? 'filtered_' : ''}${locId}.csv`; a.click(); URL.revokeObjectURL(a.href);
        });
    };

    const tiles = summary?.tiles || {};
    const configured = !!tiles.configured;
    const tileDefs = [
        ['Total bins', configured ? fmtN(tiles.total_bins) : 'Not configured'],
        ['Occupied', configured ? fmtN(tiles.occupied) : fmtN(tiles.occupied)],
        ['Empty', configured ? fmtN(tiles.empty) : 'Not configured'],
        ['Occupancy', configured ? fmtPct(tiles.occupancy_pct) : 'Not configured'],
        ['Variants', fmtN(tiles.variants)], ['Products', fmtN(tiles.products)],
        ['On hand', fmtN(tiles.total_on_hand)], ['Unrecognized values', fmtN(tiles.unrecognized)],
    ];
    const histOption = useMemo(() => ({
        grid: { left: 48, right: 16, top: 16, bottom: 32 },
        xAxis: { type: 'category', data: history.map(p => p.ran_at.slice(0, 16).replace('T', ' ')) },
        yAxis: { type: 'value', min: 0, max: 100, axisLabel: { formatter: '{value}%' } },
        tooltip: { trigger: 'axis', valueFormatter: v => `${Number(v).toFixed(1)}%` },
        series: [{ type: 'line', name: 'Occupancy', data: history.map(p => p.occupancy_pct), smooth: true, showSymbol: history.length < 40, areaStyle: { opacity: 0.08 } }],
    }), [history]);
    const aisleOptions = [{ label: 'All aisles', value: '' }, ...(summary?.aisles || []).map(a => ({ label: `Aisle ${a.aisle}`, value: a.aisle }))];
    const locName = locations.find(l => String(l.location_id) === String(locId))?.name || '';
    const level = summary?.level || 'variant';
    const toggleBinSort = (key) => { setFilters(f => ({ ...f, page: 1 })); setBinSort(s => s.key === key ? { key, dir: s.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: key === 'bin' || key === 'aisle' || key === 'overstock' || key === 'top_product' ? 'asc' : 'desc' }); };
    const arrow = (s, key) => s.key === key ? (s.dir === 'asc' ? ' ▲' : ' ▼') : '';
    const BIN_COLS = [
        { key: 'bin', label: 'Bin' }, { key: 'aisle', label: 'Aisle' }, { key: 'status', label: 'Status' },
        { key: 'variants', label: 'Variants', num: true }, { key: 'products', label: 'Products', num: true },
        { key: 'on_hand', label: 'On hand', num: true }, { key: 'overstock', label: 'Overstock bin' },
        { key: 'sold_30', label: 'Sold 30d', num: true }, { key: 'sold_90', label: 'Sold 90d', num: true },
        { key: 'last_sold', label: 'Last sold' }, { key: 'top_product', label: 'Top product' }, { key: 'variant_titles', label: 'Variant' },
    ];
    const AISLE_COLS = [
        { key: 'aisle', label: 'Aisle' }, { key: 'total', label: 'Total', num: true }, { key: 'occupied', label: 'Occupied', num: true },
        { key: 'empty', label: 'Empty', num: true }, { key: 'occupancy_pct', label: 'Occupancy', num: true },
        { key: 'variants', label: 'Variants', num: true }, { key: 'products', label: 'Products', num: true },
    ];
    const natKey = v => String(v ?? '').split(/(\d+)/).filter(Boolean).map(x => /^\d+$/.test(x) ? x.padStart(8, '0') : x.toUpperCase()).join('|');
    const sortedAisles = useMemo(() => {
        const rows = (summary?.aisles || []).slice();
        const k = aisleSort.key, d = aisleSort.dir === 'asc' ? 1 : -1;
        rows.sort((a, b) => {
            if (k === 'aisle') return d * natKey(a.aisle).localeCompare(natKey(b.aisle));
            return d * ((Number(a[k]) || 0) - (Number(b[k]) || 0));
        });
        return rows;
    }, [summary?.aisles, aisleSort]);
    const toggleAisleSort = (key) => setAisleSort(s => s.key === key ? { key, dir: s.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: key === 'aisle' ? 'asc' : 'desc' });
    const thStyle = { cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' };

    return (
        <BlockStack gap="400">
            <Card>
                <BlockStack gap="300">
                    <InlineStack align="space-between" blockAlign="center" wrap>
                        <Text variant="headingMd" as="h2">Bin Locations Report</Text>
                        <InlineStack gap="200">
                            {canDefine && locId && <Button onClick={() => setDefineOpen(true)}>Define bins…</Button>}
                            {isAdmin && locId && <Button onClick={refreshNow} disabled={!!summary?.refreshing} loading={!!summary?.refreshing}>Refresh now</Button>}
                        </InlineStack>
                    </InlineStack>
                    <Text tone="subdued">Every bin at a warehouse from the nightly snapshot: what is in it, what is empty, and what is selling. Bins come from the location's primary bin metafield; overstock bins are shown for information only.</Text>
                    <InlineStack gap="300" blockAlign="center" wrap>
                        <ReportPicker label="Location" searchable options={locations.map(l => ({ label: l.name, value: String(l.location_id) }))} value={locId} onChange={v => { setLocId(v); setFilters(f => ({ ...f, aisle: '', page: 1 })); }} />
                        <Text tone="subdued">{summary?.run ? `Bins updated as of ${fmtRun(summary.run.ran_at)}${summary.run.duration_s ? ` (snapshot took ${summary.run.duration_s >= 60 ? `${Math.round(summary.run.duration_s / 60)} min` : `${Math.round(summary.run.duration_s)} s`})` : ''}` : 'No snapshot yet'}{summary?.refreshing ? ' · refreshing…' : ''}</Text>
                    </InlineStack>
                </BlockStack>
            </Card>
            {err && <Banner tone="critical" title="Couldn't load report">{err}</Banner>}
            {summary && !configured && (
                <Banner tone="warning">No bin grid is defined for {locName}, so occupancy and empty bins cannot be counted. {canDefine ? 'Use Define bins… to add one.' : 'Ask an admin or site manager to define it.'}</Banner>
            )}

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 12 }}>
                {tileDefs.map(([label, val]) => (
                    <Card key={label}><BlockStack gap="100"><Text tone="subdued" variant="bodySm">{label}</Text><Text variant="headingLg" as="p">{val}</Text></BlockStack></Card>
                ))}
            </div>

            {configured && history.length > 0 && (
                <Card><BlockStack gap="200"><Text variant="headingMd" as="h3">Occupancy over time</Text>
                    <div style={{ height: 220 }}><ReactECharts option={histOption} style={{ height: '100%' }} opts={{ renderer: 'svg' }} /></div></BlockStack></Card>
            )}

            <div style={{ display: 'grid', gridTemplateColumns: 'minmax(260px, 1fr) minmax(320px, 2fr)', gap: 16 }}>
                <Card><BlockStack gap="200"><Text variant="headingMd" as="h3">Variants per bin</Text>
                    <table className="data-table"><thead><tr><th>Variants in bin</th><th className="col-right">Bins</th></tr></thead>
                        <tbody>{(summary?.variants_per_bin || []).map(r => (
                            <tr key={r.variants} style={r.variants > 1 && level === 'variant' ? { background: 'var(--p-color-bg-fill-warning-secondary, #fff4e5)' } : undefined}>
                                <td className="td-cell">{r.variants === 0 ? 'Empty' : r.variants}</td><td className="td-cell col-right">{fmtN(r.bins)}</td></tr>))}</tbody></table>
                </BlockStack></Card>
                {(summary?.aisles || []).length > 0 && (
                    <Card><BlockStack gap="200"><Text variant="headingMd" as="h3">Aisle by aisle</Text>
                        <div style={{ overflowX: 'auto' }}><table className="data-table" style={{ minWidth: 520 }}>
                            <thead><tr>{AISLE_COLS.map(c => (<th key={c.key} className={c.num ? 'col-right' : undefined} style={thStyle} onClick={() => toggleAisleSort(c.key)}>{c.label}{arrow(aisleSort, c.key)}</th>))}</tr></thead>
                            <tbody>{sortedAisles.map(a => (
                                <tr key={a.aisle} style={{ cursor: 'pointer' }} onClick={() => setFilters(f => ({ ...f, aisle: a.aisle, page: 1 }))}>
                                    <td className="td-cell">Aisle {a.aisle}</td><td className="td-cell col-right">{fmtN(a.total)}</td>
                                    <td className="td-cell col-right">{fmtN(a.occupied)}</td><td className="td-cell col-right">{fmtN(a.empty)}</td>
                                    <td className="td-cell col-right"><span style={{ display: 'inline-block', minWidth: 60, background: `linear-gradient(90deg, var(--p-color-bg-fill-success-secondary, #cdefd6) ${a.occupancy_pct || 0}%, transparent 0)`, padding: '0 6px', borderRadius: 4 }}>{fmtPct(a.occupancy_pct)}</span></td>
                                    <td className="td-cell col-right">{fmtN(a.variants)}</td><td className="td-cell col-right">{fmtN(a.products)}</td></tr>))}</tbody>
                        </table></div></BlockStack></Card>
                )}
            </div>

            <Card><BlockStack gap="300">
                <InlineStack align="space-between" blockAlign="center" wrap>
                    <Text variant="headingMd" as="h3">Bins {bins.total ? `(${fmtN(bins.total)})` : ''}</Text>
                    <ButtonGroup><Button onClick={() => exportCsv(false)}>Export CSV (all)</Button><Button onClick={() => exportCsv(true)}>Export CSV (filtered)</Button></ButtonGroup>
                </InlineStack>
                <InlineStack gap="300" blockAlign="end" wrap>
                    <ReportPicker label="Aisle" options={aisleOptions} value={filters.aisle} onChange={v => setFilters(f => ({ ...f, aisle: v, page: 1 }))} />
                    <ReportPicker label="Status" options={[{ label: 'All statuses', value: '' }, { label: 'Occupied', value: 'occupied' }, { label: 'Empty', value: 'empty' }]} value={filters.status} onChange={v => setFilters(f => ({ ...f, status: v, page: 1 }))} />
                    <TextField label="Search" labelHidden placeholder="Bin, SKU or product…" value={filters.q} onChange={v => setFilters(f => ({ ...f, q: v, page: 1 }))} autoComplete="off" clearButton onClearButtonClick={() => setFilters(f => ({ ...f, q: '', page: 1 }))} />
                    {level === 'variant' && <Checkbox label="More than one variant" checked={filters.multi} onChange={v => setFilters(f => ({ ...f, multi: v, page: 1 }))} />}
                </InlineStack>
                <div style={{ overflowX: 'auto' }}><table className="data-table" style={{ minWidth: 1200 }}>
                    <thead><tr>{BIN_COLS.map(c => (<th key={c.key} className={c.num ? 'col-right' : undefined} style={thStyle} onClick={() => toggleBinSort(c.key)}>{c.label}{arrow(binSort, c.key)}</th>))}</tr></thead>
                    <tbody>{bins.rows.map(r => (
                        <tr key={r.bin} style={{ cursor: 'pointer', background: r.status === 'multi' ? 'var(--p-color-bg-fill-warning-secondary, #fff4e5)' : undefined }} onClick={() => r.variants > 0 && openBin(r.bin)}>
                            <td className="td-cell"><b>{r.display}</b></td><td className="td-cell">{r.aisle || ''}</td>
                            <td className="td-cell">{r.status === 'empty' ? <Badge>Empty</Badge> : r.status === 'multi' ? <Badge tone="warning">Occupied (2+)</Badge> : <Badge tone="success">Occupied</Badge>}</td>
                            <td className="td-cell col-right">{fmtN(r.variants)}</td><td className="td-cell col-right">{fmtN(r.products)}</td>
                            <td className="td-cell col-right">{fmtN(r.on_hand)}</td><td className="td-cell" style={{ whiteSpace: 'nowrap' }}>{r.overstock || ''}</td>
                            <td className="td-cell col-right">{fmtN(r.sold_30)}</td><td className="td-cell col-right">{fmtN(r.sold_90)}</td>
                            <td className="td-cell" style={{ whiteSpace: 'nowrap' }}>{r.last_sold || (r.variants ? '—' : '')}</td>
                            <td className="td-cell">{r.top_product || ''}</td>
                            <td className="td-cell" style={{ whiteSpace: 'nowrap' }}>{r.variant_titles || ''}</td></tr>))}</tbody>
                </table></div>
                <InlineStack gap="200" blockAlign="center">
                    <Button disabled={filters.page <= 1} onClick={() => setFilters(f => ({ ...f, page: f.page - 1 }))}>← Previous</Button>
                    <Text tone="subdued">Page {filters.page} of {Math.max(1, Math.ceil(bins.total / 200))}</Text>
                    <Button disabled={filters.page * 200 >= bins.total} onClick={() => setFilters(f => ({ ...f, page: f.page + 1 }))}>Next →</Button>
                </InlineStack>
            </BlockStack></Card>

            {unrec.length > 0 && (
                <Card><BlockStack gap="200"><Text variant="headingMd" as="h3">Unrecognized bin values ({fmtN(unrec.length)})</Text>
                    <Text tone="subdued">These metafield values do not match the bin pattern. Fix them in Shopify; they are not counted anywhere above.</Text>
                    <div style={{ overflowX: 'auto' }}><table className="data-table"><thead><tr><th>Value</th><th>SKU</th><th>Product</th><th>Variant</th></tr></thead>
                        <tbody>{unrec.map((r, i) => (<tr key={i}><td className="td-cell"><code>{r.raw}</code></td><td className="td-cell">{r.shopify_url ? <a href={r.shopify_url} target="_blank" rel="noreferrer">{r.sku}</a> : r.sku}</td><td className="td-cell">{r.product_title}</td><td className="td-cell">{r.variant_title}</td></tr>))}</tbody></table></div>
                </BlockStack></Card>
            )}

            <Modal open={!!drill} onClose={() => setDrill(null)} title={drill ? `Bin ${drill.display}` : ''} size="large">
                <Modal.Section>
                    {drill && (
                        <BlockStack gap="300">
                        {/* 2026-09-13 (Eric): wide enough that Last sold is never cut off —
                            .bin-drill widens the dialog and caps the product column. */}
                            {drill.labels?.length > 0 && drill.segments?.length === drill.labels.length && (
                                <InlineStack gap="400">{drill.labels.map((l, i) => (<Text key={l} tone="subdued">{l}: <b>{drill.segments[i]}</b></Text>))}</InlineStack>
                            )}
                            <div className="bin-drill" style={{ overflowX: 'auto' }}><table className="data-table bin-drill__table" style={{ width: '100%' }}>
                                <thead><tr><th></th><th>Product</th><th>Variant</th><th>SKU</th><th className="col-right">On hand</th><th>Overstock bin</th><th className="col-right">Sold 30d</th><th className="col-right">Sold 90d</th><th style={{ whiteSpace: 'nowrap' }}>Last sold</th></tr></thead>
                                <tbody>{drill.items.map(it => (
                                    <tr key={it.variant_id}>
                                        <td className="td-cell">{it.image_proxy ? <img src={it.image_proxy} alt="" width={48} height={48} style={{ objectFit: 'cover', borderRadius: 4 }} loading="lazy" /> : null}</td>
                                        <td className="td-cell">{it.shopify_url ? <a href={it.shopify_url} target="_blank" rel="noreferrer">{it.product_title}</a> : it.product_title}{it.note ? <div><Text tone="subdued" variant="bodySm">{it.note}</Text></div> : null}</td>
                                        <td className="td-cell">{it.variant_title}</td>
                                        <td className="td-cell">{it.shopify_url ? <a href={it.shopify_url} target="_blank" rel="noreferrer">{it.sku}</a> : it.sku}</td>
                                        <td className="td-cell col-right">{fmtN(it.on_hand)}</td><td className="td-cell">{it.overstock_bin || ''}</td>
                                        <td className="td-cell col-right">{fmtN(it.sold_30)}</td><td className="td-cell col-right">{fmtN(it.sold_90)}</td>
                                        <td className="td-cell" style={{ whiteSpace: 'nowrap' }}>{it.last_sold || '—'}</td></tr>))}</tbody>
                            </table></div>
                        </BlockStack>
                    )}
                </Modal.Section>
            </Modal>

            <BinDefinitionModal open={defineOpen} onClose={() => setDefineOpen(false)} storeKey={storeKey} locationId={parseInt(locId || '0', 10)} locationName={locName}
                                onSaved={() => { loadLocations(); loadAll(); }} />
        </BlockStack>
    );
}
